import * as React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { User } from 'oidc-client';
import { Redirect, withRouter, RouteComponentProps } from 'react-router-dom';
import notify from '../../shared/feedback/notifications';
import LanguageService, {
    Translate,
    setMomentLocale,
    GetLanguageForKey,
    setDateLocale
} from '../../shared/LanguageService';
import { getUserManager } from '../../shared/userManager';
import userStore from '../../shared/UserStore';
import { loginSuccess, renewUserSession } from '../../actions/userActions';
import { getUser } from '../../reducers/userReducer';
import UserApi from '../../api/UserApi';
import { loginFailed, loadUserFromLocalStorage } from '../../actions/userActions';
import { loadLanguageSuccess } from '../../actions/languageActions';
import { loadContent } from '../../actions/contentActions';
import { getSystemVersion } from '../../reducers/sysInfoReducer';
import { impersonateCountry, unImpersonateCountry } from '../../actions/userActions';
import { getContent } from '../../reducers/contentReducer';
import { useTelemetryContext } from '../../telemetry/telemetryContext';
import { Logger } from '../../telemetry/loggers/Logger';
import { getUserInfo } from '../../telemetry/utils';
import { AuthenticatedUser, UserDetails } from '../../shared/types';

export type ImpersonatedCountry = {
    id: number;
    name: string;
};

type ReduxProps = {
    systemVersion: string;
    user: any;
    content: any;
};

type Country = {
    id: number;
    name: string;
};

type AuthActions = {
    dispatchLoginUser: (user: any) => void;
    dispatchLoginFailed: (error: any) => void;
    dispatchLoadLanguageSuccess: (language: any) => void;
    dispatchRenewUserSession: (user: any) => void;
    dispatchLoadContent: () => void;
    dispatchLoadUserFromStorage: (contentLoaded?: boolean) => void;
    dispatchImpersonateCountry: (country: Country) => void;
    dispatchUnImpersonateCountry: () => void;
};

type ReduxActions = {
    actions: AuthActions;
};

type AuthenticatedComponentState = {
    nextUrl: string | null;
};

type AuthenticatedComponentProps = {
    logger: Logger;
} & ReduxProps &
    ReduxActions &
    RouteComponentProps;

function userHasDetails(user) {
    return user && user.details;
}

export function getCurrentUserFromStorage(user: User): AuthenticatedUser {
    let currentUser: AuthenticatedUser | null = userStore.fetchUserFromLocalStorage();
    if (!currentUser) {
        currentUser = {
            ...user,
            initialized: true,
            isAuthenticated: true
        } as AuthenticatedUser;

        userStore.saveUserToLocalStorage(currentUser);
    }

    return currentUser;
}

export function withAuth(WrappedComponent: any) {
    class AuthenticatedComponent extends React.Component<
        AuthenticatedComponentProps,
        AuthenticatedComponentState
    > {
        state = { nextUrl: null };

        async componentDidMount() {
            this.initUserManager();
            await this.authenticate();
        }

        initUserManager() {
            let userManager = getUserManager();

            userManager.events.addUserLoaded(async (user: User) => {
                try {
                    await this.handleSilentRenewAuthentication(user);
                } catch (error) {
                    const { logger } = this.props;

                    const userData: AuthenticatedUser | User = user
                        ? user
                        : userStore.fetchUserFromLocalStorage();

                    const additionalInfo = {
                        origin: 'withAuth.handleAuthenticationCallback',
                        userInfo: getUserInfo(userData)
                    };

                    logger.logException(error, additionalInfo);
                }
            });
        }

        setLanguage = async (languageKey) => {
            const { actions } = this.props;
            const { dispatchLoadLanguageSuccess, dispatchLoadContent } = actions;

            const language = await GetLanguageForKey(languageKey);
            LanguageService.setLanguageInLocaleStorage(language);
            LanguageService.setLanguage(language);
            dispatchLoadLanguageSuccess(language);
            dispatchLoadContent();
        };

        updateNextUrl = (nextUrl: string) => {
            this.setState({ nextUrl });
        };

        userHasCorrectSystemVersion = (user: AuthenticatedUser) => {
            const { systemVersion } = this.props;
            return userHasDetails(user) && user.details.applicationVersion === systemVersion;
        };

        handleImpersonateCountry = () => {
            const { actions } = this.props;
            const { dispatchImpersonateCountry } = actions;
            const currentCountry = userStore.fetchCountryImpersonationFromSessionStorage();
            if (currentCountry) {
                dispatchImpersonateCountry(currentCountry);
            }
        };

        createAuthenticatedUser = async (
            currentUser: AuthenticatedUser,
            user: User
        ): Promise<AuthenticatedUser> => {
            if (this.userHasCorrectSystemVersion(currentUser)) {
                userStore.saveUserToLocalStorage(currentUser);
                return {
                    ...user,
                    initialized: true,
                    isAuthenticated: true,
                    details: currentUser.details,
                    impersonatedUser: currentUser.impersonatedUser,
                    impersonation: currentUser.impersonation
                } as AuthenticatedUser;
            }

            const userDetails: UserDetails = await UserApi.getMeDetails();

            if (window.__dateLocale !== userDetails.languageKey) {
                setDateLocale(userDetails.languageKey);
            }

            if (moment.locale() !== userDetails.languageKey) {
                setMomentLocale(userDetails.languageKey);
            }

            return {
                ...user,
                initialized: true,
                isAuthenticated: true,
                details: userDetails
            } as AuthenticatedUser;
        };

        handleAuthenticationCallback = async () => {
            const { actions } = this.props;
            const { dispatchLoadUserFromStorage, dispatchLoginUser, dispatchLoginFailed } = actions;

            let user = null;
            try {
                let userManager = getUserManager();

                user = await userManager.signinRedirectCallback();

                /**
                 * The user needs to be saved incomplete a first time
                 * in order for the Api-client to get hold of the token
                 */
                userStore.saveUserToLocalStorage({
                    ...user,
                    initialized: true,
                    isAuthenticated: true
                });

                const authenticatedUser = await this.createAuthenticatedUser(null, user);

                userStore.saveUserToLocalStorage(authenticatedUser);
                dispatchLoadUserFromStorage();

                setMomentLocale(authenticatedUser.details.languageKey);
                setDateLocale(authenticatedUser.details.languageKey);

                await this.setLanguage(authenticatedUser.details.languageKey);

                dispatchLoginUser(authenticatedUser);

                notify.success(Translate('system/toastr/logged-in'));

                this.updateNextUrl(user.state.nextUrl);
            } catch (error) {
                const { logger } = this.props;

                const userData = user ? user : userStore.fetchUserFromLocalStorage();

                const additionalInfo = {
                    origin: 'withAuth.handleAuthenticationCallback',
                    userInfo: getUserInfo(userData)
                };
                logger.logException(error, additionalInfo);
                userStore.removeUserFromStorage();
                dispatchLoginFailed(error);
            }
        };

        handleSilentRenewAuthentication = async (user: User) => {
            const { actions } = this.props;
            const { dispatchRenewUserSession, dispatchUnImpersonateCountry } = actions;

            let currentUser: AuthenticatedUser | null = null;

            try {
                let userManager = getUserManager();

                await userManager.signinSilentCallback();

                currentUser = getCurrentUserFromStorage(user);

                const authenticatedUser = await this.createAuthenticatedUser(currentUser, user);

                userStore.saveUserToLocalStorage(authenticatedUser);
                dispatchRenewUserSession(authenticatedUser);
            } catch (error) {
                const { logger } = this.props;

                const userData = currentUser ? currentUser : user;
                const additionalInfo = {
                    origin: 'withAuth.handleSilentRenewAuthentication',
                    userInfo: getUserInfo(userData)
                };
                logger.logException(error, additionalInfo);
                userStore.removeUserFromStorage();
                dispatchUnImpersonateCountry();
            }
        };

        handleAuthentication = async () => {
            const { actions } = this.props;
            const { dispatchLoadUserFromStorage, dispatchRenewUserSession } = actions;

            let user = null;
            try {
                let userManager = getUserManager();

                user = await userManager.getUser();

                if (user) {
                    const currentUser = getCurrentUserFromStorage(user);

                    const authenticatedUser = await this.createAuthenticatedUser(currentUser, user);

                    userStore.saveUserToLocalStorage(authenticatedUser);
                    let contentLoaded = this.props.content.length > 0;
                    dispatchLoadUserFromStorage(contentLoaded);

                    dispatchRenewUserSession(authenticatedUser);
                } else {
                    userStore.removeUserFromStorage();
                    const currentPath = window.location.pathname + window.location.search;

                    if (this.shouldRedirectToBrokerAgentRegistrationInfo()) {
                        this.props.history.replace(
                            `/orders/registration-info${this.props.location.search}`
                        );
                        return;
                    }

                    userManager
                        .signinRedirect({ data: { nextUrl: currentPath } })
                        .catch((error) => {
                            throw error;
                        });
                }
            } catch (error) {
                const { logger } = this.props;
                const userData = user ? user : userStore.fetchUserFromLocalStorage();

                const additionalInfo = {
                    origin: 'withAuth.handleAuthentication',
                    userInfo: getUserInfo(userData)
                };
                logger.logException(error, additionalInfo);
                userStore.removeUserFromStorage();
            }
        };

        authenticate = async () => {
            const { location } = this.props;

            if (location.pathname.includes('callback')) {
                await this.handleAuthenticationCallback();
            } else {
                await this.handleAuthentication();
            }

            this.handleImpersonateCountry();
        };

        shouldRedirectToBrokerAgentRegistrationInfo = () => {
            const { location, user } = this.props;

            if (user && user.isAuthenticated) {
                return false;
            }

            if (!location.pathname.startsWith('/orders/new')) {
                return false;
            }

            const params = new URLSearchParams(location.search);
            const brokerAgentJustAutoCreated = params.get('autocreatedbrokeragent');

            return brokerAgentJustAutoCreated === 'true';
        };

        render() {
            const { nextUrl } = this.state;
            const { user, ...rest } = this.props;
            const { location } = rest;

            if (user && user.isAuthenticated) {
                if (location.pathname.includes('callback')) {
                    return nextUrl ? <Redirect to={nextUrl} /> : null;
                }
                return <WrappedComponent {...rest} />;
            }

            return null;
        }
    }

    function AuthenticatedComponentWithLogger(props) {
        const { logger } = useTelemetryContext();
        return <AuthenticatedComponent {...props} logger={logger} />;
    }

    function mapStateToProps(state: any): ReduxProps {
        return {
            systemVersion: getSystemVersion(state),
            user: getUser(state),
            content: getContent(state)
        };
    }

    function mapDispatchToProps(dispatch: any): ReduxActions {
        return {
            actions: {
                dispatchLoginUser: (user) => {
                    dispatch(loginSuccess(user));
                },
                dispatchLoginFailed: (error) => {
                    dispatch(loginFailed(error));
                },
                dispatchRenewUserSession: (user) => {
                    dispatch(renewUserSession(user));
                },
                dispatchLoadLanguageSuccess: (language) => {
                    dispatch(loadLanguageSuccess(language));
                },
                dispatchLoadContent: () => {
                    dispatch(loadContent());
                },
                dispatchLoadUserFromStorage: (contentLoaded) => {
                    dispatch(loadUserFromLocalStorage(contentLoaded));
                },
                dispatchImpersonateCountry: (country) => {
                    dispatch(impersonateCountry(country));
                },
                dispatchUnImpersonateCountry: () => {
                    dispatch(unImpersonateCountry());
                }
            }
        };
    }

    return connect(
        mapStateToProps,
        mapDispatchToProps
    )(withRouter(AuthenticatedComponentWithLogger));
}
